import React, { useEffect, useState } from "react";
import { Modal, Button, Accordion } from "react-bootstrap";
import API from "../../config/axios";
import { api_paths } from "../../config/api.paths";
import DatePicker from "../common/DatePicker";
import SelectField from "../common/SelectField";
import { validateData, clearError } from "../../validators/YupValidations";
import { memberSchema } from "../../validators/validatorSchema";
import { formatUSPhone, normalizePhoneForSave } from "../../utils/phoneFormat";
import { useDispatch, useSelector } from "react-redux";
import { SET_LOADING } from "../../redux/user";
import editIcon from "../../assets/images/icons/edit.svg";
import deleteIcon from "../../assets/images/icons/delete.svg";
import ConfirmDeleteModal from "../common/ConfirmDeleteModal";
import MembershipPaymentModal from "./MembershipPaymentModal";
import { toastSuccessMessage, toastErrorMessage } from "../../utils/utils";

const Field = ({ label, required, children, error }) => (
  <div className="mb-3 pb-1">
    <label className="form-label small mb-2">
      {label} {required && <span className="text-danger">*</span>}
    </label>
    {children}
    {error && <div className="text-danger small mt-1">{error}</div>}
  </div>
);

const FamilyMemberModal = ({ show, onHide, editData, refreshList, inactiveMembers = [] }) => {
  const initialForm = {
    first_name: "",
    middle_name: "",
    last_name: "",
    date_of_birth: null,
    gender: "",
    relationship_id: "",
    marital_status: null,
    phone: "",
    email: "",
    country_code: "+1",
  };

  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [relationships, setRelationships] = useState([]);
  const { loading } = useSelector((state) => state.USER_DATA);

  // Delete Confirmation State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentMembers, setPaymentMembers] = useState([]);
  const [plan, setPlan] = useState(null);
  const dispatch = useDispatch();

  const isFormFilled = () => {
    // Only consider form "filled" if a name is entered. 
    // This allows users to "cancel" a partial entry (like just a dropdown selected) by clearing the name,
    // avoiding validation blocking the payment flow.
    return (
      form.first_name && form.first_name.trim() !== ""
    );
  };

  /* ---------- Load Relationships ---------- */
  const loadRelationships = async () => {
    try {
      dispatch(SET_LOADING(true));
      const res = await API.get(
        api_paths.member.family_member.GET_RELATIONSHIP
      );
      setRelationships(res.data?.payload?.result || []);
    } finally {
      dispatch(SET_LOADING(false));
    }
  };

  const fetchPlan = async () => {
    try {
      dispatch(SET_LOADING(true));
      const planRes = await API.get(api_paths.member.membership.GET_PLAN);

      const plans = planRes.data?.payload?.result || [];
      if (plans.length > 0) setPlan(plans[0]);
    } catch (err) {
      console.error("Error loading data:", err);
    } finally {
      dispatch(SET_LOADING(false)); // STOP LOADING
    }
  };

  useEffect(() => {
    if (show) {
        loadRelationships();
         // If editing an existing active member (passed via editData), load it.
        // But for "Add Member" flow, form is clean, list is strictly inactiveMembers
        if (editData) {
            setForm({
              ...editData,
                 phone:
                editData.country_code === "+1"
                    ? formatUSPhone(editData.phone || "")
                    : editData.phone || "",
            });
            setEditingId(editData._id);
        }
    }
    // eslint-disable-next-line
  }, [show, editData]);

  /* ---------- Handle Change ---------- */
  const handleChange = (field, value) => {
    let nextValue = value;

    if (field === "phone") {
      if (form.country_code === "+1") {
        nextValue = formatUSPhone(value);
      } else {
        nextValue = value.replace(/\D/g, "").slice(0, 15);
      }
    }

    let updatedForm = { ...form, [field]: nextValue };

    // spouse → married logic
    if (field === "relationship_id") {
      const selectedRel = relationships.find((r) => r._id === value);
      if (selectedRel?.key === "spouse") {
        updatedForm.marital_status = "married";
      } else {
        updatedForm.marital_status = null;
      }
    }

    setForm(updatedForm);
    if (errors[field]) clearError(setErrors, field);
  };

  /* ---------- Save / Add API Call ---------- */
  const saveMember = async () => {
      dispatch(SET_LOADING(true));
      try {
        const payload = {
            first_name: form.first_name,
            middle_name: form.middle_name,
            last_name: form.last_name,
            date_of_birth: form.date_of_birth,
            gender: form.gender,
            relationship_id: form.relationship_id,
            marital_status: form.marital_status,
            email: form.email,
            country_code: form.country_code,
            phone:
              form.country_code === "+1"
                ? normalizePhoneForSave(form.phone)
                : form.phone.replace(/\D/g, ""),
          };

          let response;
          if (editingId) {
             // Update
             response = await API.put(api_paths.member.family_member.UPDATE_MEMBER + "/" + editingId, payload);
          } else {
             // Add
             response = await API.post(api_paths.member.family_member.ADD_MEMBER, payload);
          }

          if (response.data?.success || response.data?.payload?.result) {
              toastSuccessMessage(editingId ? "Member updated successfully!" : "Member added successfully!");
              await refreshList(); // Refresh parent list
              setForm(initialForm); // Clear form
              setErrors({}); // Clear errors
              setEditingId(null);
              return true; // Success
          }
      } catch (error) {
           toastErrorMessage(error?.response?.data?.message || "Failed to save member.");
           console.error(error);
           return false;
      } finally {
          dispatch(SET_LOADING(false));
      }
  };


  /* ---------- Save & Add Another ---------- */
  const handleSaveAndAddAnother = async () => {
    const isValid = await validateData(memberSchema, form, setErrors);
    if (!isValid) return;

    await saveMember();
  };

  const handleEditFromList = (member) => {
    setErrors({}); // Clear any previous validation errors
    setForm({
      ...member,
      phone:
        member.country_code === "+1"
          ? formatUSPhone(member.phone || "")
          : member.phone || "",
    });
    setEditingId(member._id);
  };

  const handleDeleteCallback = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (deleteId) {
        try {
            dispatch(SET_LOADING(true));
            await API.delete(`${api_paths.member.family_member.DELETE_MEMBER}/${deleteId}`);
            toastSuccessMessage("Member deleted successfully.");
            await refreshList();
             // If we ruled deleting the currently editing one, clear form
             if (editingId === deleteId) {
                 setForm(initialForm);
                 setEditingId(null);
             }
        } catch (error) {
             toastErrorMessage("Failed to delete member.");
        } finally {
            dispatch(SET_LOADING(false));
            setDeleteId(null);
            setShowDeleteModal(false);
        }
    }
  };

  /* ---------- Proceed To Payment ---------- */
  const handleProceedToPayment = async () => {
    // 1. If form has data OR we are editing an existing member, validate and save
    if (isFormFilled() || editingId) {
      const isValid = await validateData(memberSchema, form, setErrors);
      if (!isValid) return; // Stop if invalid

      const success = await saveMember();
      if (!success) return; // Stop if save failed
    } else {
      // If we are bypassing save (empty form in Add mode), clear any stale errors
      setErrors({});
    }

    // 2. Prepare for payment (include all inactive members)
    // Note: Since saveMember() calls refreshList(), inactiveMembers prop might technically be stale
    // for a few milliseconds, but usually React updates fast enough. 
    // However, to be safe, we might just rely on the API flow or user clicking again. 
    // BUT for better UX, we assume refreshList() finished.
    // Also, if we just saved one, it SHOULD be in the inactiveMembers list now (after refresh).
    // Just in case verify logic:
    
    // We'll rely on the updated prop. But `refreshList` is async.
    // So we need to ensure the parent state updated.
    
    // Actually, simply opening the payment modal with `inactiveMembers` is tricky if the prop hasn't updated yet.
    // Hack: Wait a bit or fetch list locally? No, refreshList awaits.
    
    // Re-check prop:
    // If we just saved, the list passed to PaymentModal needs to include that new one.
    // Since we called `await refreshList()`, React should re-render with new props.
    // The `setShowPaymentModal` might happen before the re-render potentially? 
    // No, `await refreshList` finishes, then we set state.
    // But Parent Re-render -> Child Re-render -> Then we proceed?
    // We might need to delay opening payment modal until the list updates?
    
    // Let's assume standard behavior: `refreshList` in parent updates state, which triggers re-render of THIS component with new `inactiveMembers`.
    // THEN we open modal?
    // Actually, we can't easily wait for a re-render here inside the function.
    
    // WORKAROUND: We will pass `inactiveMembers` but if we just saved something, we assume it's there.
    // Alternatively, we can assume the user will see the accordion update and then click "Proceed" again?
    // No, the button says "Proceed to Payment".
    
    // Better: Fetch the list ourselves directly here just for the Payment Modal to be safe?
    // Or just use the prop. If logic is correct, `await refreshList` runs `fetchMembers` in parent. `fetchMembers` sets state. Parent renders. Child receives new props.
    // But this function `handleProceedToPayment` continues executing. It holds the OLD `inactiveMembers` in closure?
    // YES. `inactiveMembers` inside this function is the OLD value.
    
    // Fix: We need to fetch the latest list to pass to PaymentModal.
    // Or rely on `useEffect` to open modal?
    // Let's fetch latest list here just to be safe for payment.
    
    try {
        dispatch(SET_LOADING(true));
        const res = await API.get(api_paths.member.family_member.GET_MEMBER);
        const allMembers = res.data?.payload?.result || [];
        // Filter for payment
        // We use the same logic as parent to ensure consistency
        const toPay = allMembers.filter(m => {
             // Logic: Not active and not free
             const isFree = m?.is_free_membership;
             const isActive = m?.is_membership_active;
             // Expiry logic check (if needed, but usually !isActive covers it)
             // Parent used: !isActive or Expired.
             // If !isActive, it covers expired.
             // So:
             return !isActive && !isFree;
        });

        if (toPay.length === 0) {
            toastSuccessMessage("No pending members to pay for.");
            dispatch(SET_LOADING(false));
            return;
        }

        await fetchPlan(); // Gets plan info
        setPaymentMembers(toPay);
        setShowPaymentModal(true);
    } catch (e) {
        console.error(e);
    } finally {
        dispatch(SET_LOADING(false));
    }
  };

  const handleCancel = () => {
    setForm(initialForm); // Clear form on close? User asked: "refresh ... already saved member stay... if i add half info ... still there"
    // "if i add half information and close the modal and again open then this halfed added information still there"
    // So DO NOT clear form here.
    onHide();
    // But "refresh or change page then remove information" -> handled by React state memory.
  };

  /* ---------- Options ---------- */
  const genderOptions = [
    { value: "male", label: "Male" },
    { value: "female", label: "Female" },
    { value: "other", label: "Other" },
  ];

  const maritalStatusOptions = [
    { value: "single", label: "Single" },
    { value: "married", label: "Married" },
    { value: "divorced", label: "Divorced" },
    { value: "widowed", label: "Widowed" },
  ];

  const countryCodeOptions = [
    { value: "+1", label: "+1" },
    { value: "+91", label: "+91" },
  ];

  return (
    <Modal show={show} onHide={handleCancel} size="xl" centered className={`
    ${showPaymentModal || showDeleteModal ? "hide-modal-dialog" : ""}
    responsive-payment-modal
  `} >
      <Modal.Header closeButton>
        <Modal.Title>Manage Family Members</Modal.Title>
      </Modal.Header>

      <div className="scroll-wrapper">
         {/* ---------- Inactive List Accordion ---------- */}
         {inactiveMembers.length > 0 && (
             <div className="px-md-4 px-2 pt-3 mb-3">
                 <Accordion defaultActiveKey="0">
                    <Accordion.Item eventKey="0" className="border-0 accordian-item">
                        <Accordion.Header className="accordian-header mb-2">
                             <h6 className="anual-event-title mb-0">
                                Pending Payment ({inactiveMembers.length})
                             </h6>
                        </Accordion.Header>
                        <Accordion.Body className="p-0 pt-2">
                             {inactiveMembers.map((m, i) => (
                                <div
                                    key={i}
                                    className="d-flex justify-content-between align-items-center border px-3 py-2 mb-3 accordian-body"
                                >
                                    <div>
                                        <div className="fw-semibold">{m.first_name} {m.last_name}</div>
                                        <small className="text-muted">
                                            {relationships.find((r) => r._id === m.relationship_id)?.name || "Member"}
                                        </small>
                                    </div>

                                    <div className="d-flex gap-3">
                                        <span
                                        className="cursor-pointer text-primary"
                                        onClick={() => handleEditFromList(m)}
                                        title="Edit"
                                        >
                                            <img src={editIcon} alt="edit" />
                                        </span>
                                        <span
                                        className="cursor-pointer text-danger"
                                        onClick={() => handleDeleteCallback(m._id)}
                                        title="Delete"
                                        >
                                            <img src={deleteIcon} alt="delete" />
                                        </span>
                                    </div>
                                </div>
                             ))}
                        </Accordion.Body>
                    </Accordion.Item>
                 </Accordion>
             </div>
         )}


        <Modal.Body className="px-md-4 px-2 pt-0">
          {/* ---------- Subtitle ---------- */}
          <h6 className="anual-event-title mb-3 mt-3 px-2">
            {editingId ? "Edit Family Member" : "Add New Family Member"}
          </h6>

          {/* ---------- FULL FORM ---------- */}
          <div className="row pt-2 px-2">
            <div className="col-md-12">
              <Field label="Relationship" required>
                <SelectField
                  value={
                    relationships
                      .map((r) => ({ value: r._id, label: r.name }))
                      .find((o) => o.value === form.relationship_id) || null
                  }
                  options={relationships.map((r) => ({
                    value: r._id,
                    label: r.name,
                  }))}
                  onChange={(opt) => handleChange("relationship_id", opt.value)}
                  error={errors.relationship_id?.message}
                />
              </Field>
            </div>
            <div className="col-md-4">
              <Field
                label="First Name"
                required
                error={errors.first_name?.message}
              >
                <input
                  type="text"
                  className={`form-control form-control-sm ${errors.first_name ? "is-invalid" : ""
                    }`}
                  value={form.first_name}
                  onChange={(e) => handleChange("first_name", e.target.value)}
                  placeholder="Enter first name"
                />
              </Field>
            </div>

            <div className="col-md-4">
              <Field
                label="Middle Name/Initial"
                error={errors.middle_name?.message}
              >
                <input
                  type="text"
                  className={`form-control form-control-sm ${errors.middle_name ? "is-invalid" : ""
                    }`}
                  value={form.middle_name}
                  onChange={(e) => handleChange("middle_name", e.target.value)}
                  placeholder="Enter middle name"
                />
              </Field>
            </div>

            <div className="col-md-4">
              <Field label="Last Name" required error={errors.last_name?.message}>
                <input
                  type="text"
                  className={`form-control form-control-sm ${errors.last_name ? "is-invalid" : ""
                    }`}
                  value={form.last_name}
                  onChange={(e) => handleChange("last_name", e.target.value)}
                  placeholder="Enter last name"
                />
              </Field>
            </div>

            <div className="col-md-4">
              <Field label="Date of Birth" required>
                <DatePicker
                  futureNotAllowed={true}
                  value={form.date_of_birth}
                  onChange={(date) => handleChange("date_of_birth", date)}
                  error={errors.date_of_birth?.message}
                />
              </Field>
            </div>

            <div className="col-md-4">
              <Field label="Gender" required>
                <SelectField
                  value={
                    genderOptions.find((o) => o.value === form.gender) || null
                  }
                  options={genderOptions}
                  onChange={(opt) => handleChange("gender", opt.value)}
                  error={errors.gender?.message}
                />
              </Field>
            </div>

            <div className="col-md-4">
              <Field
                label="Marital Status"
                error={errors.marital_status?.message}
              >
                <SelectField
                  value={
                    maritalStatusOptions.find(
                      (o) => o.value === form.marital_status
                    ) || null
                  }
                  options={maritalStatusOptions}
                  onChange={(opt) => handleChange("marital_status", opt.value)}
                />
              </Field>
            </div>

            <div className="col-md-6">
              <Field label="Mobile Number">
                <div className="d-flex gap-2">
                  <div style={{ width: "120px" }}>
                    <SelectField
                      value={countryCodeOptions.find(
                        (opt) => opt.value === form.country_code
                      )}
                      options={countryCodeOptions}
                      onChange={(opt) => {
                        const newCode = opt.value;
                        setForm((prev) => ({
                          ...prev,
                          country_code: newCode,
                          phone:
                            newCode === "+1"
                              ? formatUSPhone(prev.phone)
                              : prev.phone.replace(/\D/g, ""),
                        }));
                      }}
                    />
                  </div>

                  <input
                    type="tel"
                    placeholder="Enter Mobile Number"
                    className={`form-control form-control-sm ${errors.phone ? "is-invalid" : ""
                      }`}
                    value={form.phone}
                    onChange={(e) => handleChange("phone", e.target.value)}
                    maxLength={form.country_code === "+1" ? 15 : 10}
                  />
                </div>

                {errors.phone && (
                  <div className="invalid-feedback d-block">
                    {errors.phone.message}
                  </div>
                )}
              </Field>
            </div>

            <div className="col-md-6">
              <Field label="Email" error={errors.email?.message}>
                <input
                  type="email"
                  className={`form-control form-control-sm ${errors.email ? "is-invalid" : ""
                    }`}
                  value={form.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  placeholder="Enter email"
                />
              </Field>
            </div>
          </div>
          <div className="my-3 px-2">
            <Button
              className="btn btn-grey-lg me-3 fw-semibold mb-3 mb-md-0"
              onClick={handleSaveAndAddAnother}
              disabled={loading}
            >
              {editingId ? "Update & Add Another" : "Save & Add Another"}
            </Button>

            <Button
              className="btn btn-orange-lg fw-semibold"
              onClick={handleProceedToPayment}
            >
              Proceed to Payment
            </Button>
          </div>
        </Modal.Body>
      </div>

      {showPaymentModal && plan && (
        <MembershipPaymentModal
          show={showPaymentModal}
          onHide={() => setShowPaymentModal(false)}
          members={paymentMembers}
          plan={plan}
          onBack={() => setShowPaymentModal(false)}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        show={showDeleteModal}
        title="Delete Member"
        message="Are you sure you want to delete this family member? This action cannot be undone."
        onConfirm={confirmDelete}
        onCancel={() => setShowDeleteModal(false)}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </Modal>
  );
};

export default FamilyMemberModal;
